import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Flex,
  Skeleton,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Tooltip,
  Wrap,
  WrapItem,
  Avatar,
  Select,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaHome, FaCity, FaFilter, FaSync } from 'react-icons/fa';
import { GiModernCity } from 'react-icons/gi';
import { MdLocationCity } from 'react-icons/md';
import { useRegionData } from '../../../../contexts/RegionDataContext';

const VillageSection = ({ onCountUpdate }) => {
  const [searchInput, setSearchInput] = useState('');
  const [filterTahsil, setFilterTahsil] = useState('');

  const {
    villages,
    tahsils,
    isLoading,
    searchVillages,
    refreshVillages,
    fetchVillages,
    counts,
  } = useRegionData();

  const searchTimeoutRef = useRef(null);

  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const gradientStart = useColorModeValue('teal.500', 'teal.600');
  const gradientEnd = useColorModeValue('cyan.500', 'cyan.600');
  const accentColor = useColorModeValue('teal.500', 'teal.300');
  const tableHeaderBg = useColorModeValue('teal.600', 'teal.700');
  const rowHoverBg = useColorModeValue('blue.50', 'gray.700');
  const headingColor = useColorModeValue('teal.800', 'teal.200');

  useEffect(() => {
    if (tahsils.length > 0 && !filterTahsil) {
      setFilterTahsil(tahsils[0]._id);
    }
  }, [tahsils, filterTahsil]);

  const filteredVillages = useMemo(() => {
    return villages.filter(
      (v) =>
        v.name?.toLowerCase().includes(searchInput.toLowerCase()) &&
        (filterTahsil ? v.tahsil?._id === filterTahsil : true),
    );
  }, [villages, searchInput, filterTahsil]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchVillages(value, filterTahsil);
    }, 500);
  };

  const handleTahsilChange = (e) => {
    const value = e.target.value;
    setFilterTahsil(value);
    setSearchInput('');
    fetchVillages('', value);
  };

  const handleRefresh = () => {
    refreshVillages(filterTahsil);
    setSearchInput('');
  };

  useEffect(() => {
    if (onCountUpdate && counts.village > 0) {
      onCountUpdate(counts.village);
    }
  }, [counts.village, onCountUpdate]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const selectedTahsil = useMemo(() => {
    return tahsils.find((t) => t._id === filterTahsil);
  }, [tahsils, filterTahsil]);

  return (
    <Box>
      <Card
        mb={4}
        borderRadius="lg"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="6px"
          bgGradient={`linear(to-r, ${gradientStart}, ${gradientEnd})`}
        />

        <CardBody p={4}>
          <Flex justify="space-between" align="center" mb={4}>
            <Flex align="center" gap={3}>
              <Box
                p={3}
                borderRadius="xl"
                bg="green.100"
                _dark={{ bg: 'green.900' }}
                position="relative"
                overflow="hidden"
              >
                <Icon as={FaHome} boxSize={5} color={accentColor} />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Heading size="md" color={headingColor}>
                  Villages
                </Heading>
                <Text fontSize="xs" color={subtitleColor}>
                  {counts.village} total villages
                </Text>
              </VStack>
            </Flex>
          </Flex>

          <Flex gap={3} wrap="wrap" align="center" mb={3}>
            <InputGroup size="md" flex="1" minW="250px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={accentColor} boxSize={4} />
              </InputLeftElement>
              <Input
                placeholder="Search by village name..."
                value={searchInput}
                onChange={handleSearchChange}
                color={textColor}
                borderRadius="lg"
                border="2px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: accentColor,
                  boxShadow: `0 0 0 1px ${accentColor}`,
                }}
                fontSize="sm"
              />
            </InputGroup>

            <Flex gap={2} align="center">
              <Select
                placeholder="Tahsil"
                value={filterTahsil}
                onChange={handleTahsilChange}
                size="md"
                borderRadius="lg"
                minW="200px"
                border="2px solid"
                borderColor={borderColor}
                _focus={{
                  borderColor: accentColor,
                  boxShadow: `0 0 0 1px ${accentColor}`,
                }}
              >
                {tahsils.map((tahsil) => (
                  <option key={tahsil._id} value={tahsil._id}>
                    {tahsil.name}
                  </option>
                ))}
              </Select>
            </Flex>

            <HStack spacing={3}>
              <Tooltip label="Refresh data">
                <Flex
                  as="button"
                  p={2}
                  borderRadius="full"
                  bg={`${accentColor}10`}
                  onClick={handleRefresh}
                  cursor="pointer"
                  align="center"
                  justify="center"
                  _hover={{ bg: `${accentColor}20` }}
                  transition="all 0.2s"
                >
                  <Icon as={FaSync} color={accentColor} boxSize={4} />
                </Flex>
              </Tooltip>
              <Badge
                colorScheme="teal"
                variant="subtle"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
              >
                {filteredVillages.length} results
              </Badge>
            </HStack>
          </Flex>

          {(searchInput || filterTahsil) && (
            <Flex gap={2} align="center" mt={2}>
              <Badge
                colorScheme="gray"
                variant="subtle"
                borderRadius="full"
                px={2}
                py={1}
                fontSize="xs"
              >
                <Flex align="center" gap={1}>
                  <Icon as={FaFilter} boxSize={2} />
                  Filters:
                </Flex>
              </Badge>
              <Wrap spacing={1}>
                {searchInput && (
                  <WrapItem>
                    <Badge
                      colorScheme="teal"
                      borderRadius="full"
                      px={2}
                      py={1}
                      fontSize="xs"
                    >
                      {searchInput}
                    </Badge>
                  </WrapItem>
                )}
                {filterTahsil && selectedTahsil && (
                  <WrapItem>
                    <Badge
                      colorScheme="purple"
                      borderRadius="full"
                      px={2}
                      py={1}
                      fontSize="xs"
                    >
                      {selectedTahsil.name}
                    </Badge>
                  </WrapItem>
                )}
              </Wrap>
            </Flex>
          )}

          <Card
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            bg={cardBg}
            overflow="hidden"
            mt={3}
          >
            {isLoading.villages ? (
              <Box p={4}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} height="50px" borderRadius="md" mb={2} />
                ))}
              </Box>
            ) : filteredVillages.length === 0 ? (
              <CardBody>
                <VStack spacing={4} p={8} textAlign="center">
                  <Icon as={FaHome} boxSize={12} color={accentColor} />
                  <VStack spacing={2}>
                    <Heading size="md" color={headingColor}>
                      {villages.length === 0
                        ? 'No Villages Found'
                        : 'No Matching Records'}
                    </Heading>
                    <Text fontSize="sm" color={subtitleColor} maxW="400px">
                      {villages.length === 0
                        ? 'The database is currently empty.'
                        : 'Try adjusting your search term or filter criteria.'}
                    </Text>
                  </VStack>
                </VStack>
              </CardBody>
            ) : (
              <>
                <Box
                  overflowY="auto"
                  maxH="500px"
                  css={{
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                      background: 'gray.100',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: accentColor,
                      borderRadius: '3px',
                    },
                  }}
                >
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} zIndex={1}>
                      <Tr bg={tableHeaderBg}>
                        <Th
                          textAlign="center"
                          color="white"
                          fontSize="xs"
                          py={3}
                          width="80px"
                        >
                          #
                        </Th>
                        <Th color="white" fontSize="xs" py={3} pl={4}>
                          VILLAGE
                        </Th>
                        <Th color="white" fontSize="xs" py={3}>
                          TAHSIL
                        </Th>
                        <Th color="white" fontSize="xs" py={3}>
                          CIRCLE
                        </Th>
                        <Th color="white" fontSize="xs" py={3}>
                          SUB-DIVISION
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredVillages.map((village, index) => (
                        <Tr
                          key={village._id}
                          _hover={{ bg: rowHoverBg }}
                          cursor="pointer"
                        >
                          <Td textAlign="center" py={3} width="80px">
                            <Badge
                              colorScheme="teal"
                              variant="solid"
                              borderRadius="full"
                              px={2}
                              py={1}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              {index + 1}
                            </Badge>
                          </Td>
                          <Td py={3} pl={4}>
                            <Flex align="center" gap={3}>
                              <Avatar
                                size="sm"
                                name={village.name}
                                bg="teal.500"
                                color="white"
                                fontSize="sm"
                              />
                              <Box flex="1">
                                <Text
                                  fontWeight="semibold"
                                  color={textColor}
                                  fontSize="sm"
                                  noOfLines={1}
                                >
                                  {village.name?.toUpperCase() || 'N/A'}
                                </Text>
                              </Box>
                            </Flex>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={2}>
                              <Icon
                                as={FaCity}
                                color="blue.500"
                                fontSize="sm"
                              />
                              <Text fontSize="sm">
                                {village.tahsil?.name?.toUpperCase() || '-'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={2}>
                              <Icon
                                as={MdLocationCity}
                                color="purple.500"
                                fontSize="sm"
                              />
                              <Text fontSize="sm">
                                {village.circle?.name?.toUpperCase() || '-'}
                              </Text>
                            </Flex>
                          </Td>
                          <Td py={3}>
                            <Flex align="center" gap={2}>
                              <Icon
                                as={GiModernCity}
                                color="orange.500"
                                fontSize="sm"
                              />
                              <Text fontSize="sm">
                                {village.subdivision?.name?.toUpperCase() ||
                                  '-'}
                              </Text>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Box
                  py={2}
                  px={4}
                  borderTop="1px solid"
                  borderColor={borderColor}
                >
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={subtitleColor}>
                      Showing {filteredVillages.length} villages
                    </Text>
                    <Text fontSize="xs" color={subtitleColor}>
                      Updated:{' '}
                      {new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </Flex>
                </Box>
              </>
            )}
          </Card>
        </CardBody>
      </Card>
    </Box>
  );
};

export default React.memo(VillageSection);
